<template>
    <div class="self-form-wrap">
        <div class="w-1/3 self-form-wrap-item">
            <label class="self-form-item-label" for="paev">{{ label }}</label>
            <div class="relative">
                <div class="self-form-select-wrap">
                    <select class="e5-select" name="paev" :id="'paev-' + uniqueId" v-model="selectedDay" :disabled="disable">
                        <option v-for="day in filteredDays" :key="`${uniqueId}-${day}`" :value="day">{{ day }}</option>
                    </select>
                </div>
            </div>
        </div>

        <div class="w-1/3 self-form-wrap-item">
            <label class="self-form-item-label" for="kuu">&nbsp;</label>
            <div class="relative">
                <div class="self-form-select-wrap">
                    <select class="e5-select" name="kuu" :id="'kuu-' + uniqueId" v-model="selectedMonth" :disabled="disable">
                        <option v-for="(month, index) in filteredMonths" :key="`${uniqueId}-${index}`" :value="month">{{ month }}</option>
                    </select>
                </div>
            </div>
        </div>

        <div class="w-1/3 self-form-wrap-item">
            <label class="self-form-item-label" for="aasta">&nbsp;</label>
            <div class="relative">
                <div class="self-form-select-wrap">
                    <select class="e5-select" name="aasta" :id="'aasta-' + uniqueId" v-model="selectedYear" :disabled="disable">
                        <option v-for="year in filteredYears" :key="`${uniqueId}-${year}`" :value="year">{{ year }}</option>
                    </select>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import moment from 'moment';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component
export default class Dob extends Vue {
    @Prop(String) label!: string;
    @Prop(Boolean) isDob!: boolean;
    @Prop(String) presetDate!: string;
    @Prop(Boolean) disable!: boolean;
    @Prop(Boolean) hasPastAndFutureDates!: boolean;
    @Prop(String) uniqueId!: string;

    days: string[] = Array.from({ length: 31 }, (_, i) => (i + 1).toString());
    months: string[] =
        this.$i18n.locale === 'et'
            ? [
                  'jaanuar',
                  'veebruar',
                  'märts',
                  'aprill',
                  'mai',
                  'juuni',
                  'juuli',
                  'august',
                  'september',
                  'oktoober',
                  'november',
                  'detsember'
              ]
            : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    years: string[] = [];

    selectedDay: string | null = new Date().getDate().toString();
    selectedMonth: string | null = this.months[new Date().getMonth()];
    selectedYear: string | null = new Date().getFullYear().toString();

    mounted(): void {
        this.setYears();
        this.updateDays();
        // this.updateFromPreset();
    }

    @Watch('presetDate', { immediate: true })
    onPresetDateChange() {
        this.updateFromPreset();
    }

    private updateFromPreset() {
        if (this.presetDate) {
            const presetDate = moment(this.presetDate, 'DD.MM.YYYY');
            if (presetDate.year() - moment().year() > 10) {
                // Preselected date is too far in the future to display in the selection
                this.selectedDay = '';
                this.selectedMonth = '';
                this.selectedYear = '';
            } else {
                this.selectedDay = String(presetDate.date());
                this.selectedMonth = this.months[presetDate.month()];
                this.selectedYear = presetDate.year().toString();
            }
        }
    }

    @Watch('isDob')
    @Watch('hasPastAndFutureDates')
    private setYears(): void {
        const currentYear = new Date().getFullYear();
        if (this.isDob) {
            this.years = Array.from({ length: 125 }, (_, i) => (currentYear - i).toString()).reverse();
        } else if (this.hasPastAndFutureDates) {
            const pastYears = Array.from({ length: 25 }, (_, i) => (currentYear - i).toString());
            const futureYears = Array.from({ length: 11 }, (_, i) => (currentYear + i).toString());
            this.years = Array.from(new Set([...pastYears.reverse(), ...futureYears]));
        } else {
            this.years = Array.from({ length: 11 }, (_, i) => (currentYear + i).toString());
        }
    }

    @Watch('selectedDay')
    @Watch('selectedMonth')
    @Watch('selectedYear')
    private updateDays(): void {
        if (this.selectedMonth && this.selectedYear) {
            const monthIndex = this.months.indexOf(this.selectedMonth);
            const year = parseInt(this.selectedYear);

            if (!isNaN(monthIndex) && !isNaN(year)) {
                const daysInMonth = this.getDaysInMonth(year, monthIndex);
                this.days = Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
            }
        }
        this.emitSelectedDate();
    }

    @Watch('$i18n.locale')
    onLocaleChange() {
        this.months =
            this.$i18n.locale === 'et'
                ? [
                      'jaanuar',
                      'veebruar',
                      'märts',
                      'aprill',
                      'mai',
                      'juuni',
                      'juuli',
                      'august',
                      'september',
                      'oktoober',
                      'november',
                      'detsember'
                  ]
                : [
                      'January',
                      'February',
                      'March',
                      'April',
                      'May',
                      'June',
                      'July',
                      'August',
                      'September',
                      'October',
                      'November',
                      'December'
                  ];
    }

    private getDaysInMonth(year, month) {
        return new Date(parseInt(year), parseInt(month) + 1, 0).getDate();
    }

    private emitSelectedDate() {
        if (this.selectedDay && this.selectedMonth && this.selectedYear) {
            const day = this.selectedDay.padStart(2, '0');
            const month = (this.months.indexOf(this.selectedMonth) + 1).toString().padStart(2, '0');
            const year = this.selectedYear;
            const selectedDate = `${year}-${month}-${day}`;
            this.$emit('update:date', selectedDate);
        }
    }

    /**
     * Filter out days before current date for setting valid_until dates that have to be in the future
     */
    get filteredDays() {
        if (!this.isDob && this.selectedYear && this.selectedMonth) {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth();
            const currentDay = currentDate.getDate();

            if (parseInt(this.selectedYear) === currentYear && this.months.indexOf(this.selectedMonth) === currentMonth) {
                return this.days.filter((day) => parseInt(day) >= currentDay);
            }
        }
        return this.days;
    }

    /**
     * Filter out months before current date for setting valid_until dates that have to be in the future
     */
    get filteredMonths() {
        if (!this.isDob && this.selectedYear) {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth();

            if (parseInt(this.selectedYear) === currentYear) {
                return this.months.filter((_, index) => index >= currentMonth);
            }
        }
        return this.months;
    }

    get filteredYears() {
        return this.years;
    }
}
</script>

<style scoped>
.self-form-wrap {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    padding-top: 0rem;
    margin-bottom: 0rem;
}
</style>
